import { useSnapshot } from "valtio";
import { useNavigate } from "react-router-dom";
import { editorState } from "../../../../utils/editor";
import { accessCardState } from "../../../../stores/accessCardStore";
import { formatDateForPreview } from "../../../../utils/dateUtils";
import TWButton from "../../../../components/TWButton";

interface EventSelectionDesignOverlayProps {
  onEventSelected: (eventId: string, eventType: string) => void;
}

const EventSelectionDesignOverlay = ({ onEventSelected }: EventSelectionDesignOverlayProps) => {
  const { eventSchedules } = useSnapshot(editorState);
  const { selectedEvent } = useSnapshot(accessCardState);
  const navigate = useNavigate();

  const handleEventSelect = (eventId: string, eventType: string) => {
    onEventSelected(eventId, eventType);
  };

  const handleCreateEvent = () => {
    navigate("/website/edit/event-schedule");
  };

  return (
    <div className="flex items-start justify-center pt-16 px-4">
      <div className="max-w-xs w-full">
        <div className="text-center mb-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-2 uppercase tracking-wide">
            SELECT EVENT
          </h2>
          <p className="text-sm text-gray-600">
            Choose the event for your access cards
          </p>
        </div>

        {eventSchedules && eventSchedules.length > 0 ? (
          <div className="space-y-3 mb-8">
            {eventSchedules.map((schedule) => (
              <button
                key={schedule._id}
                onClick={() => handleEventSelect(schedule._id, schedule.eventType)}
                className={`
                  w-full text-left border rounded-lg p-4 cursor-pointer transition-all hover:shadow-md
                  ${selectedEvent === schedule._id
                    ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-500 ring-opacity-20'
                    : 'border-gray-200 hover:border-gray-300'
                  }
                `}
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900 mb-1 text-sm">
                      {schedule.eventType}
                    </h3>
                    <p className="text-xs text-gray-600">
                      {formatDateForPreview(schedule.date)} • {`${schedule.rawDate.hours.toString().padStart(2, '0')}:${schedule.rawDate.minutes.toString().padStart(2, '0')}`}
                    </p>
                    {schedule.address && (
                      <p className="text-xs text-gray-500 mt-1 truncate">
                        {schedule.address}
                      </p>
                    )}
                  </div>
                  {selectedEvent === schedule._id && (
                    <div className="ml-3">
                      <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                  )}
                </div>
              </button>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 mb-8">
            <svg className="w-12 h-12 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 002 2z" />
            </svg>
            <h3 className="text-sm font-medium text-gray-900 mb-2 uppercase tracking-wide">No Events Available</h3>
            <p className="text-sm text-gray-600 mb-6">
              Create events in your wedding schedule first.
            </p>
            <TWButton
              onClick={handleCreateEvent}
              variant="primary"
              size="lg"
            >
              CREATE EVENT
            </TWButton>
          </div>
        )}

      </div>
    </div>
  );
};

export default EventSelectionDesignOverlay;
